import { useEffect, useState, FormEvent } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/lib/supabase";

const SUBSCRIBED_KEY = "newsletter-popup:suscrito";
const seenKey = (path: string) => `newsletter-popup:visto:${path}`;

// El almacenamiento puede no existir (modo privado, cookies bloqueadas): nunca debe romper la página.
function readStorage(kind: "local" | "session", key: string) {
  try {
    return (kind === "local" ? localStorage : sessionStorage).getItem(key);
  } catch {
    return null;
  }
}

function writeStorage(kind: "local" | "session", key: string) {
  try {
    (kind === "local" ? localStorage : sessionStorage).setItem(key, "1");
  } catch {
    // sin almacenamiento: el pop-up puede volver a salir, no pasa nada
  }
}

/**
 * Invitación al newsletter que aparece al entrar a una sección.
 * Sale una vez por sección en cada visita y nunca más cuando la persona ya se suscribió.
 */
export default function NewsletterPopup({ delayMs = 5000 }: { delayMs?: number }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (readStorage("local", SUBSCRIBED_KEY) || readStorage("session", seenKey(pathname))) return;
    const timer = window.setTimeout(() => {
      setOpen(true);
      writeStorage("session", seenKey(pathname));
    }, delayMs);
    return () => window.clearTimeout(timer);
  }, [pathname, delayMs]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !name) {
      setError("Por favor completa todos los campos.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      const { error: supaError } = await supabase
        .from("newsletter_subscribers")
        .insert([{ email, name }]);

      if (supaError) {
        if (supaError.message.includes("duplicate")) {
          writeStorage("local", SUBSCRIBED_KEY);
          setError("Este correo ya está suscrito al newsletter.");
          return;
        }
        throw supaError;
      }

      try {
        await supabase.functions.invoke("send-welcome-email", {
          body: { email, name },
        });
      } catch (emailErr) {
        console.error("Error sending welcome email:", emailErr);
      }

      writeStorage("local", SUBSCRIBED_KEY);
      setSubmitted(true);
    } catch {
      setError("Error de conexión. Inténtalo de nuevo.");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) setOpen(false);
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="newsletter-popup-title"
        className="relative w-full max-w-md bg-[#2563EB] rounded-2xl p-6 md:p-8 text-center shadow-2xl overflow-hidden"
      >
        <div className="absolute -left-16 -top-16 w-48 h-48 rounded-full bg-white/5" />
        <div className="absolute -right-20 -bottom-20 w-64 h-64 rounded-full bg-white/5" />

        <button
          type="button"
          onClick={() => setOpen(false)}
          aria-label="Cerrar"
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
        >
          <i className="ri-close-line" />
        </button>

        <div className="relative z-10">
          <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mx-auto mb-4">
            <i className="ri-mail-send-line text-white text-xl" />
          </div>
          <h2
            id="newsletter-popup-title"
            className="text-white font-bold mb-2"
            style={{ fontFamily: "'Barlow Condensed', sans-serif", fontSize: "1.8rem" }}
          >
            SUSCRÍBETE AL NEWSLETTER
          </h2>
          <p className="text-white/75 font-inter text-sm mb-6">
            Recibe las noticias más importantes del sector aéreo directamente en tu correo.
          </p>

          {submitted ? (
            <div className="bg-white rounded-xl px-6 py-6">
              <div className="w-11 h-11 flex items-center justify-center bg-[#2563EB] rounded-full mx-auto mb-3">
                <i className="ri-check-line text-white text-lg" />
              </div>
              <p className="text-[#1e3a5f] font-bold text-base font-inter">¡Suscripción exitosa!</p>
              <p className="text-slate-500 text-sm font-inter mt-1">
                Pronto recibirás las últimas noticias del sector aeronáutico dominicano.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <input
                type="text"
                name="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Tu nombre"
                className="w-full px-4 py-3 rounded-lg bg-white text-slate-800 placeholder-slate-400 text-sm font-inter focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Tu correo electrónico"
                className="w-full px-4 py-3 rounded-lg bg-white text-slate-800 placeholder-slate-400 text-sm font-inter focus:outline-none focus:ring-2 focus:ring-white/50"
              />
              {error && <p className="text-red-100 text-xs font-inter text-left">{error}</p>}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-white hover:bg-slate-100 text-[#2563EB] font-inter font-semibold text-sm rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin" /> Enviando...
                  </span>
                ) : (
                  <>
                    <i className="ri-send-plane-line mr-2" /> Suscribirme
                  </>
                )}
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-white/60 hover:text-white text-xs font-inter transition-colors cursor-pointer"
              >
                No, gracias
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
